import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    FormDropdown,
    mapToDropdownOption,
    FormMultiselectDropdown,
    ValidationResult,
    FormValidationSummary,
    Checkbox
} from "sprint-common-web";

import { FormDistributorSearchDropdown } from "../../../../../common/components/form/FormDistributorDropdown";
import { Distributor } from "../../../../../common/data/lookup-data-providers/DistributorsDataProvider";
import { VersionType } from "../../../../../common/data/lookup-data-providers/VersionTypesDataProvider";
import { EditMovieVersionFormInputs } from "./EditMovieVersionFormInputs";
import { useEditMovieVersionForm } from "./useEditMovieVersionForm";

export interface EditMovieVersionFormProps {
    movieVersionId: string;
    onCancel(): void;
    onSubmit(updatedVersionData: UpdatedVersionData): void;
}

export interface UpdatedVersionData {
    versionId: string;
    distributor: Distributor;
    type: VersionType;
    isTrailer: boolean;
}

export const EditMovieVersionForm: React.FunctionComponent<EditMovieVersionFormProps> = (
    props: EditMovieVersionFormProps
) => {
    const {
        defaultValues,
        defaultOptions,
        versionTypes,
        trailerVersionTypes,
        isTrailerSelected,
        setIsTrailerSelected,
        airlines,
        selectedMovieVersion,
        validateEditMovieVersion,
        editMovieVersion
    } = useEditMovieVersionForm(props.movieVersionId);

    const [validationResult, setValidationResult] = useState<ValidationResult>();

    const { errors, control, formState, handleSubmit } = useForm<EditMovieVersionFormInputs>({
        defaultValues,
        criteriaMode: "all"
    });

    const handleEditMovieVersion = async (inputs: EditMovieVersionFormInputs) => {
        setValidationResult(undefined);

        const result = await validateEditMovieVersion(inputs);
        setValidationResult(result);

        if (!result.isValid) {
            return;
        }

        const updatedVersion = await editMovieVersion(inputs);

        props.onSubmit(updatedVersion);
    };

    const handleCancelClick = () => {
        props.onCancel();
    };

    const handleTrailerVersionToggle = (_: unknown, isChecked?: boolean) => {
        if (!trailerVersionTypes) return;

        setIsTrailerSelected(isChecked ?? false);
        control.setValue("versionTypeId", isChecked ? trailerVersionTypes[0].id : undefined);
    };

    return (
        <Form onSubmit={handleSubmit(handleEditMovieVersion)}>
            <FormFields>
                <FormDistributorSearchDropdown
                    initOptions={defaultOptions.distributors}
                    control={control}
                    errors={errors}
                    rules={{ required: "Search and select distributor" }}
                    name="distributorId"
                    label="Distributor"
                    placeholder={"Distributor"}
                    disabled={!!selectedMovieVersion?.buzzVersion}
                />
                <FormDropdown
                    options={versionTypes?.map((item) => mapToDropdownOption(item)) ?? []}
                    label="Version Type"
                    control={control}
                    name="versionTypeId"
                    errors={errors}
                    rules={{ required: "Search and select version type" }}
                    disabled={isTrailerSelected || !!selectedMovieVersion?.buzzVersion}
                />

                <FormMultiselectDropdown
                    options={airlines.map((item) => mapToDropdownOption(item))}
                    label="Brandings"
                    control={control}
                    name={"brandingIds"}
                    errors={errors}
                    disabled={true}
                />

                <Checkbox
                    label="Trailer version"
                    name="isTrailerSelected"
                    checked={isTrailerSelected}
                    onChange={handleTrailerVersionToggle}
                    testId="trailer-version-selected"
                    disabled={!!selectedMovieVersion?.buzzVersion}
                />
            </FormFields>
            {validationResult && <FormValidationSummary validationResult={validationResult} />}
            <FormActions isLoading={formState.isSubmitting || formState.isValidating} onCancel={handleCancelClick} />
        </Form>
    );
};
